import type { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import {
  API_B2C_DEVELOPMENT_BASE_URL,
  API_B2C_EXPORT_ORDERS_PATH,
  API_B2C_ORDERS_PATH,
  API_B2C_ORDERS_STATUS_PATH,
  API_B2C_PRODUCTION_BASE_URL,
  API_B2C_STAGING_BASE_URL,
  ROWS_PER_PAGE,
} from '~/constants';
import { getAppEnv } from '~/constants/env';
import { Logger } from '~/lib/logger';
import { useAuth } from '~/providers/AuthProvider';
import { B2CApiResponseSchema } from '~/types/B2COrdersData';
import type { OrdersTableDataType, OrdersTableStatusType } from '~/types/OrdersTableData';
import { convertToTableData, mapTableStatusToB2CStatusIds } from '~/utils';

const logger = new Logger('B2C Orders Hook');

export const useB2COrders = () => {
  const { token, logout } = useAuth();
  const [ordersData, setOrdersData] = useState<OrdersTableDataType | null>(null);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [isApproveLoading, setIsApproveLoading] = useState<boolean>(false);
  const [isRejectLoading, setIsRejectLoading] = useState<boolean>(false);
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>(searchTerm);
  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [endDate, setEndDate] = useState<DateTime | null>(null);
  const [page, setPage] = useState<number>(0);
  const [status, setStatus] = useState<OrdersTableStatusType>('all');
  const [totalItems, setTotalItems] = useState<number>(0);

  const appEnv = getAppEnv();
  const baseB2CApiUrl =
    appEnv === 'production'
      ? API_B2C_PRODUCTION_BASE_URL
      : appEnv === 'staging'
        ? API_B2C_STAGING_BASE_URL
        : API_B2C_DEVELOPMENT_BASE_URL;

  /**
   * Fetches B2C Orders data from the API
   */
  const fetchData = useCallback(async () => {
    setIsDataLoading(true);
    setIsError(false);
    setError(null);

    try {
      const start = startDate?.toUTC().toISO();
      const end = endDate?.toUTC().toISO();
      const statusCodes = status === 'all' ? null : mapTableStatusToB2CStatusIds(status);

      const queryParams = new URLSearchParams({
        limit: '20',
        offset: (page * ROWS_PER_PAGE).toString(),
        ...(debouncedSearchTerm && { filter: debouncedSearchTerm }),
        ...(start && { start_date: start }),
        ...(end && { end_date: end }),
      });

      if (statusCodes?.length) {
        statusCodes.forEach((code, index) => {
          queryParams.append(`order_status[${index}]`, code.toString());
        });
      }

      const response = await fetch(`${baseB2CApiUrl}${API_B2C_ORDERS_PATH}?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // If the request is unauthorized, the user will be redirected to the login page via the RequireAuth component
        if (response.status === 401) {
          logger.error('Unauthorized request');
          logout();
        }

        setOrdersData(null);
        throw new Error('Failed to fetch Orders data');
      }

      const responseData = await response.json();
      const parsedResponseData = B2CApiResponseSchema.safeParse(responseData);
      if (!parsedResponseData.success) {
        logger.error('Parsing data error', parsedResponseData.error.errors);
        throw new Error('Parsing data error: invalid response data');
      }

      setTotalItems(parsedResponseData.data.data.pagination.total || 0);
      const tableData = parsedResponseData.data ? convertToTableData(parsedResponseData.data) : null;

      setOrdersData(tableData);
    } catch (error) {
      logger.error('Failed to fetch Orders data', error);
      setIsError(true);
      setError('Failed to fetch Orders data');
    } finally {
      setIsDataLoading(false);
    }
  }, [baseB2CApiUrl, endDate, logout, page, debouncedSearchTerm, startDate, status, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Updates the status of the selected items, either approving or rejecting them
   * @param ids - array of order item ids to update
   * @param status - status to update the items to
   */
  const updateItemsStatus = useCallback(
    async (ids: string[], status: 'approved' | 'rejected') => {
      if (status === 'approved') {
        setIsApproveLoading(true);
      } else {
        setIsRejectLoading(true);
      }
      setIsError(false);
      setError(null);

      try {
        const response = await fetch(`${baseB2CApiUrl}${API_B2C_ORDERS_STATUS_PATH}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            orderItemIds: ids,
            isApproved: status === 'approved',
            isResubmitted: false,
          }),
        });

        if (!response.ok) {
          // If the request is unauthorized, the user will be redirected to the login page via the RequireAuth component
          if (response.status === 401) {
            logger.error('Unauthorized request');
            logout();
          }

          throw new Error('Failed to update items status');
        }

        fetchData();
      } catch (error) {
        logger.error('Failed to update items status', error);
        setIsError(true);
        setError('Failed to update items status');
      } finally {
        if (status === 'approved') {
          setIsApproveLoading(false);
        } else {
          setIsRejectLoading(false);
        }
      }
    },
    [baseB2CApiUrl, fetchData, logout, token],
  );

  const handleApproveItems = useCallback(
    async (ids: string[]) => {
      await updateItemsStatus(ids, 'approved');
    },
    [updateItemsStatus],
  );

  const handleRejectItems = useCallback(
    async (ids: string[]) => {
      await updateItemsStatus(ids, 'rejected');
    },
    [updateItemsStatus],
  );

  /**
   * Sends a request to export Orders data
   * @param startDate - start date for the export
   * @param endDate - end date for the export
   */
  const requestSupplierOrdersExport = useCallback(
    async (startDate: DateTime, endDate: DateTime, successCallback?: () => void) => {
      setIsExportLoading(true);
      setIsError(false);
      setError(null);

      const errorMessage = 'Failed to export orders data';
      const startDateIso = startDate.toUTC().toISO();
      const endDateIso = endDate.toUTC().toISO();

      try {
        if (!startDateIso || !endDateIso) {
          throw new Error('Invalid date range');
        }

        const queryParams = new URLSearchParams({
          start_date: startDateIso,
          end_date: endDateIso,
        });

        const response = await fetch(`${baseB2CApiUrl}${API_B2C_EXPORT_ORDERS_PATH}?${queryParams}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          // If the request is unauthorized, the user will be redirected to the login page via the RequireAuth component
          if (response.status === 401) {
            logger.error('Unauthorized request');
            logout();
          }

          throw new Error(errorMessage);
        }

        successCallback && successCallback();
      } catch (error) {
        setIsError(true);
        setError(errorMessage);
        logger.error(errorMessage, error);
      } finally {
        setIsExportLoading(false);
      }
    },
    [baseB2CApiUrl, logout, token],
  );

  return {
    ordersData,
    isDataLoading,
    isApproveLoading,
    isRejectLoading,
    isError,
    error,
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    setDebouncedSearchTerm,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    page,
    setPage,
    status,
    setStatus,
    totalItems,
    handleApproveItems,
    handleRejectItems,
    requestSupplierOrdersExport,
    isExportLoading,
  };
};
